/**
 * Validation utilities for common input fields
 */

/**
 * Validates an email address
 * @param email The email to validate
 * @returns true if valid, false otherwise
 */
export function isValidEmail(email: string | null | undefined): boolean {
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (trimmed.length === 0) return false;
  
  // Basic email validation: local-part@domain
  // More permissive than strict RFC 5322, but catches common errors
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  // Additional checks
  if (!emailRegex.test(trimmed)) return false;
  
  // Check for valid structure
  const parts = trimmed.split('@');
  if (parts.length !== 2) return false;
  
  const [localPart, domain] = parts;
  
  // Local part (before @) should not be empty and max 64 chars
  if (localPart.length === 0 || localPart.length > 64) return false;
  
  // Domain should not be empty and max 255 chars
  if (domain.length === 0 || domain.length > 255) return false;
  
  // Domain should have at least one dot
  if (!domain.includes('.')) return false;
  
  // Check for consecutive dots
  if (trimmed.includes('..')) return false;
  
  // Check for invalid start/end characters
  if (localPart.startsWith('.') || localPart.endsWith('.')) return false;
  if (domain.startsWith('.') || domain.endsWith('.')) return false;
  if (domain.startsWith('-') || domain.endsWith('-')) return false;
  
  return true;
}

/**
 * Validates a website URL
 * @param url The URL to validate
 * @returns true if valid, false otherwise
 */
export function isValidWebsite(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (trimmed.length === 0) return false;
  
  try {
    // Allow URLs without protocol
    const urlToTest = trimmed.match(/^https?:\/\//i) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(urlToTest);
    
    // Must have a valid hostname with at least one dot or be localhost
    return parsed.hostname.includes('.') || parsed.hostname === 'localhost';
  } catch {
    return false;
  }
}

/**
 * Validates a phone number (international format)
 * Accepts: +49 123 456789, 0049 123 456789, 123456789, +49-123-456789, etc.
 * @param phone The phone number to validate
 * @returns true if valid, false otherwise
 */
export function isValidPhone(phone: string | null | undefined): boolean {
  if (!phone || typeof phone !== 'string') return false;
  const trimmed = phone.trim();
  if (trimmed.length === 0) return false;
  
  // Remove all spaces, dashes, parentheses for validation
  const cleaned = trimmed.replace(/[\s\-()]/g, '');
  
  // Must contain only digits, +, and optionally start with 00
  if (!/^(\+|00)?\d{7,15}$/.test(cleaned)) return false;
  
  return true;
}

/**
 * Validates a SEPA Creditor ID (Gläubiger-Identifikationsnummer)
 * Format: DE98ZZZ09999999999 (ISO 13616)
 * @param creditorId The creditor ID to validate
 * @returns true if valid, false otherwise
 */
export function isValidCreditorId(creditorId: string | null | undefined): boolean {
  if (!creditorId || typeof creditorId !== 'string') return false;
  const trimmed = creditorId.trim().toUpperCase();
  
  // SEPA Creditor ID format: 2 letter country code + 2 check digits + 3 char business code + national identifier
  // Length: typically 18 characters for Germany (DE)
  // Example: DE98ZZZ09999999999
  
  if (trimmed.length < 8 || trimmed.length > 35) return false;
  
  // Must start with 2 letter country code
  if (!/^[A-Z]{2}/.test(trimmed)) return false;
  
  // Next 2 characters must be digits (check digits)
  if (!/^[A-Z]{2}\d{2}/.test(trimmed)) return false;
  
  // Rest must be alphanumeric
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(trimmed)) return false;
  
  // Simple format validation only - no check digit verification
  return true;
}

